import { ChevronDownIcon } from "lucide-react";
import { DiscFlightArt } from "@/components/app/disc-flight-art";
import { TournamentStatusBadge } from "@/components/app/tournament-status-badge";
import { useT } from "@/i18n";
import { dateRangeOf } from "@/i18n/format";
import type { TournamentSummary } from "../../../shared/tournament";
import type { ScheduledGame, Team } from "../../../shared/types";

/** Fixel leads the event identity; the flight illustration stays separate
 * from the title and live data so long tournament names can wrap freely. */
export function TournamentBanner({
  meta,
  teams,
  schedule,
  pulse,
  liveUnavailable,
  compact = false,
}: {
  meta: TournamentSummary;
  teams: readonly Team[] | undefined;
  schedule: readonly ScheduledGame[] | undefined;
  pulse: { total: number; completed: number; live: number; goals: number } | undefined;
  liveUnavailable: boolean;
  /** The live match leads; event-wide figures remain one disclosure away. */
  compact?: boolean;
}) {
  const t = useT();
  const place = [meta.city, meta.venue].filter(Boolean).join(" · ");
  const facts = [
    { label: t("board.view.teams"), count: teams?.length },
    {
      label: t("board.banner.rosterEntries"),
      count: teams?.reduce((total, team) => total + (team.roster?.length ?? 0), 0),
    },
    {
      label: t("board.banner.completed"),
      count: pulse ? `${pulse.completed} / ${pulse.total}` : undefined,
    },
    { label: t("board.banner.goals"), count: pulse?.goals },
  ];

  if (compact) {
    return (
      <section aria-labelledby="tournament-title" className="space-y-2">
        <div className="flex items-start justify-between gap-4">
          <h1
            id="tournament-title"
            className="nxt min-w-0 text-xl leading-tight text-pretty uppercase [overflow-wrap:anywhere] sm:text-3xl"
          >
            {meta.name}
          </h1>
          <span className="hidden shrink-0 sm:block">
            <TournamentStatusBadge compact meta={meta} schedule={schedule ?? []} />
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground sm:text-sm">
          {place && <span>{place}</span>}
          <span>{dateRangeOf(meta.startDate, meta.endDate)}</span>
          {pulse !== undefined && pulse.live > 0 && (
            <span className="font-semibold text-go-ink">
              {t("spectator.live", { count: pulse.live })}
            </span>
          )}
          {liveUnavailable && <span>{t("spectator.liveUnavailable")}</span>}
        </div>
        <details className="group/overview">
          <summary className="flex w-fit cursor-pointer list-none items-center gap-1 py-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring pointer-coarse:min-h-11 [&::-webkit-details-marker]:hidden">
            {t("board.live.eventOverview")}
            <ChevronDownIcon
              aria-hidden="true"
              className="size-3.5 group-open/overview:rotate-180"
            />
          </summary>
          <dl className="grid grid-cols-2 gap-3 pt-3 sm:grid-cols-4">
            {facts.map(({ label, count }) => (
              <div key={label} className="rounded-xl bg-card px-3 py-2.5">
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="nxt mt-1 text-xl tabular-nums">{count ?? "—"}</dd>
              </div>
            ))}
          </dl>
        </details>
      </section>
    );
  }

  return (
    <section aria-labelledby="tournament-title" className="font-sans">
      <div className="relative py-5 sm:grid sm:grid-cols-[minmax(0,1fr)_14rem] sm:items-center sm:gap-8 sm:py-4 adm:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-3">
          <div className="flex min-h-10 items-center pr-28 sm:min-h-0 sm:pr-0">
            <TournamentStatusBadge compact meta={meta} schedule={schedule ?? []} />
          </div>
          <h1
            id="tournament-title"
            className="nxt max-w-full text-[clamp(1.75rem,5vw,4rem)] leading-tight text-balance uppercase [overflow-wrap:anywhere]"
          >
            {meta.name}
          </h1>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-2 w-24 sm:relative sm:top-auto sm:w-auto"
        >
          <DiscFlightArt />
        </div>
      </div>
      <div className="grid gap-4 border-t py-4 adm:grid-cols-[minmax(0,1fr)_auto] adm:items-center adm:gap-6">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-5 gap-y-1 text-xs sm:text-sm">
          {place && <span className="text-muted-foreground [overflow-wrap:anywhere]">{place}</span>}
          <span className="font-semibold">{dateRangeOf(meta.startDate, meta.endDate)}</span>
          {pulse !== undefined && pulse.live > 0 && (
            <span className="font-semibold text-go-ink dark:text-go">
              {t("spectator.live", { count: pulse.live })}
            </span>
          )}
          {liveUnavailable && (
            <span className="text-muted-foreground">{t("spectator.liveUnavailable")}</span>
          )}
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4 sm:gap-8">
          {facts.map(({ label, count }) => (
            <div key={label} className="flex min-w-0 flex-col-reverse justify-end gap-1">
              <dt className="text-[11px] leading-snug text-muted-foreground">{label}</dt>
              <dd className="text-2xl font-semibold leading-none tracking-tight tabular-nums">
                {count ?? "—"}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      {pulse !== undefined && pulse.total > 0 && (
        <div
          role="progressbar"
          aria-label={t("spectator.pulse")}
          aria-valuemin={0}
          aria-valuemax={pulse.total}
          aria-valuenow={pulse.completed}
          aria-valuetext={t("spectator.progress", {
            completed: pulse.completed,
            total: pulse.total,
          })}
          className="h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full bg-brand"
            style={{ width: `${(100 * pulse.completed) / pulse.total}%` }}
          />
        </div>
      )}
    </section>
  );
}
